import { UserRoles } from '@onereach/auth-ui-module';
import { AccountData } from '@or-sdk/accounts';
import { User, IdentityProvider } from '@or-sdk/auth';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { authUiUrl } from '@/utils/authUiRuntime';
import { getRootWindow } from '@/utils/sso';

import accountsService from '../../api/accounts';
import authService from '../../api/auth';
import settingsService from '../../api/settings';
import usersService from '../../api/users';
import config from '../../config';
import router from '../../router/router';
import { usePaymentsStore } from '../payments';

import {
  AccountsItem,
  AuthData,
  AuthInitState,
  RecoverSessionParams,
  SignInParams,
  UpdateReactiveTokenParams,
  UserPermissions,
} from './types';

const cookieMonitorLogoutCallback = (callback: (value: boolean) => void) => () => {
  callback(true);
};


export const useAuthStore = defineStore('auth', () => {
  // state
  const token = () => {
    return authService.getUserToken() as string | null;
  };
  const reactiveToken = ref<string | null>(null);
  const tokenExpireTime = ref<number | null>(null);
  const accountId = ref<string | null>(null);
  const username = ref<string | null>(null);
  const userId = ref<string | null>(null);
  const error = ref<unknown>(null);
  const authorized = ref<boolean | null>(null);
  const multiUserId = ref<string | null>(null);
  const role = ref<UserRoles | null>(null);
  const initialPath = ref<string | null | undefined>(null);
  const redirectPath = ref<string | null>(null);
  const destinationUrl = ref<string | null>(null);
  const allowGuest = ref(false);
  const twoFactorCheck = ref(false);
  const twoFactorEnabled = ref<null | boolean | undefined>(false);
  const identityProvider = ref<IdentityProvider | null>(null);
  const multiUserIsLoggingUser = ref(false);
  const multiUser = ref<User | null>(null);
  const availableUsers = ref<AccountsItem[]>([]);
  const cookiesExpired = ref(false);
  const cookiesAnotherUser = ref(false);
  const cookiesNoRights = ref(false);
  const sso_needs_two_factor = ref(false);
  const isSSO = ref(false);
  const sso_key = ref<string | null>(null);
  const permissions = ref<UserPermissions | undefined>(undefined);
  const isReadonly = ref(false);
  const isAdmin = ref(true);
  const isPayable = ref(false);
  const isSuspended = ref(false);
  const forceChangePassword = ref(false);

  const _authCookiesExpired = (value: boolean) => {
    cookiesExpired.value = value;
    cookiesAnotherUser.value = false;
    cookiesNoRights.value = false;
  };

  const _authAnotherUser = () => {
    cookiesExpired.value = false;
    cookiesAnotherUser.value = true;
    cookiesNoRights.value = false;
  };

  const _authNoRights = () => {
    cookiesExpired.value = false;
    cookiesAnotherUser.value = false;
    cookiesNoRights.value = true;
  };

  const _authInit = (initState: Partial<AuthInitState>) => {
    accountId.value = initState.accountId ?? null;
    multiUserId.value = initState.multiUserId ?? null;
    username.value = initState.username ?? null;
    userId.value = initState.userId ?? null;
    role.value = initState.role ?? null;
    authorized.value = initState.authorized ?? null;
    error.value = initState.error ?? null;
    initialPath.value = initState.initialPath;
    identityProvider.value = initState.identityProvider ?? null;
    twoFactorEnabled.value = initState.twoFactorEnabled ?? false;
    permissions.value = initState.permissions ?? undefined;
    reactiveToken.value = initState.token ?? null;
    isReadonly.value = (role.value === 'USER' && permissions.value?.scope?.includes('READONLY')) || false;
    isAdmin.value = role.value === 'ADMIN' || role.value === 'SUPER_ADMIN';
  };

  const _setIsPayable = (value: boolean) => {
    isPayable.value = value;
  };

  const _authSuccess = (authData: AuthData) => {
    accountId.value = authData.accountId;
    username.value = authData.username;
    userId.value = authData.userId;
    role.value = authData.role;
    multiUserId.value = authData.multiUserId;
    identityProvider.value = authData.identityProvider;
    twoFactorEnabled.value = authData.twoFactorEnabled;
    reactiveToken.value = authData.token;
    authorized.value = true;
    twoFactorCheck.value = false;
    error.value = null;
    sso_key.value = null;
  };

  // TODO: Refactor and remove event-based errors, process errors in different way.
  const _authError = (_error: Error | string | unknown) => {
    accountId.value = null;
    username.value = null;
    userId.value = null;
    role.value = null;
    identityProvider.value = null;
    authorized.value = false;
    error.value = _error;
    multiUserId.value = null;
  };

  const _signOut = () => {
    accountId.value = null;
    username.value = null;
    userId.value = null;
    role.value = null;
    authorized.value = false;
    error.value = null;
    identityProvider.value = null;
    twoFactorCheck.value = false;
    initialPath.value = null;
    multiUserId.value = null;
    reactiveToken.value = null;
    sso_needs_two_factor.value = false;
    multiUser.value = null;
    sso_key.value = null;
    isPayable.value = false;
    isSuspended.value = false;
    forceChangePassword.value = false;
    destinationUrl.value = null;
  };

  const _twoFactorCheck = (value: boolean) => {
    twoFactorCheck.value = value;
  };

  const _setRedirectUrl = (value: string) => {
    redirectPath.value = value;
  };

  const _setDestinationUrl = (value: string | null) => {
    destinationUrl.value = value;
  };

  const _multiUserIsLogginUser = (value: boolean) => {
    multiUserIsLoggingUser.value = value;
  };

  const _authSetMultiUser = (value: User) => {
    multiUser.value = value;
  };

  const _multiUserToggleMFA = () => {
    if (multiUser.value) {
      multiUser.value.twoFactorCheck = !multiUser.value.twoFactorCheck;
    }
  };

  const _multiUserAvailableUsers = (users: AccountsItem[]) => {
    availableUsers.value = users;
  };

  const _setIsSso = () => {
    isSSO.value = true;
  };

  const _ssoNeedsTwoFactor = ({ error: _error, KEY }: { error: Error; KEY: string; }) => {
    twoFactorCheck.value = true;
    error.value = _error;
    sso_needs_two_factor.value = true;
    if (KEY) {
      sso_key.value = KEY;
    }
  };

  const _authSuspended = ({ suspended }: { suspended: boolean; }) => {
    isSuspended.value = suspended;
  };

  const _setForceChangePassword = (value: boolean) => {
    forceChangePassword.value = value;
  };

  const _cookieMonitorLoginCallback = () => (newUser: User) => {
    if (!userId.value || (newUser?.userId ?? null) === userId.value) {
      if (cookiesExpired.value) {
        _authCookiesExpired(false);
      }
      if (cookiesAnotherUser.value) {
        _authCookiesExpired(true);
      }
    } else {
      if (!cookiesAnotherUser.value) {
        _authAnotherUser();
      }
    }
  };

  const updateReactiveToken = ({ token, expire }: UpdateReactiveTokenParams) => {
    reactiveToken.value = token;
    tokenExpireTime.value = expire;
  };

  const init = async ({ initialPath, clearCookies = true }: { initialPath: string; clearCookies?: boolean; }) => {
    authService.tokenUpdateCallback = (user) => {
      updateReactiveToken(user as UpdateReactiveTokenParams);
    };
    // if initial path starts with '/login' then replace it with '/'
    initialPath = initialPath.match(/^\/login/) ? '/' : initialPath;

    const callback = (user: User) => {
      authService.monitorCookie(
        cookieMonitorLogoutCallback(_authCookiesExpired),
        _cookieMonitorLoginCallback()
      );
      _authInit({
        ...user,
        authorized: true,
        initialPath,
      });

      authService.validateMultiUserToken().catch(console.warn);
    };

    return authService.validateUser(true)
      .then(user => callback(user))
      .catch(async (error) => {
        if (error.cause?.response?.data?.message.includes('account is suspended')) {
          // TODO: change to straight up value;
          // TODO: change naming;
          _authSuspended({ suspended: true });
          // retry user valitaion with suspended route
          const userOrError = await authService.validateUser(true, false, true).catch(error => error as Error);
          if (!(userOrError instanceof Error)) {
            callback(userOrError);
            return userOrError;
          }
          else error = userOrError;
        }

        if (clearCookies)
          authService.signOut();
        authService.monitorCookie(
          () => { },
          (loggedInUser) => {
            // TODO: refactor
            _authInit({
              ...loggedInUser,
              authorized: true,
              initialPath,
            });
          });

        _authInit({
          authorized: false,
          initialPath,
        });
        if (error.errorType === 'NoRightsError') {
          _authNoRights();
        }
        throw error;
      });
  };

  const refreshSuspendedUserToken = async () => {
    await authService.refreshUserToken();
    return authService.validateUser(true).then(user => {
      _authSuspended({ suspended: false });
      _authInit({
        ...user,
        authorized: true,
      });
    })
      .catch(async (error) => {
        if (error.cause?.response?.data?.message.includes('account is suspended')) {
          _authSuspended({ suspended: true });
        } else {
          console.warn(error);
        }
      });
  };

  const signIn = async ({
    credentials,
    keepLoggedIn,
    verificationCode,
    captchaToken,
    isCaptchaV2Checkbox,
    rememberTwoFactor,
    accountData,
    googleCode,
  }: SignInParams) => {
    return authService.signIn({
      long: keepLoggedIn,
      allowGuestLogin: allowGuest.value,
      ...!googleCode && { credentials },
      captchaToken,
      isCaptchaV2Checkbox,
      verificationCode,
      rememberTwoFactor,
      googleCode,
    })
      .then(async result => {
        if (result.twoFactorCheck) {
          _twoFactorCheck(true);
        } else {
          if (result.tokenType === 'multi-user') {
            await validateMultiUser();
            return await authorizeMult({
              keepLoggedIn,
              accountData,
            });
          }
          // Edgecase for single users;
          // TODO: Test how single users operate in depth;
          _authSuccess(result as unknown as AuthData);
        }
      })
      .catch(error => {
        _authError(error);
        throw error;
      });
  };

  const _checkProfile = async (params) => {
    const phoneRegex = /^[0-9()+-]*$/;
    const phoneOrEmpty = helpers.withMessage('Invalid phone number', (value: string) => {
      // Allow empty values or values that match the regex
      return !value || phoneRegex.test(value);
    });
    const profile = (await authService.multiUserGetProfile()).data?.profile || {};
    const validations: Record<string, unknown> = {
      phoneOrEmpty,
      required,
    };
    const profileFields = Object.fromEntries(Object.keys(config.profileFields).map(x => [x, ref(profile[x] || '')]));


    const rules = {
      ...Object.fromEntries(Object.entries(config.profileFields)
        .map(([key, validationKeys]) => {
          return [
            key,
            Object.fromEntries(
              (validationKeys as string[])
                .filter(validationKey => validationKey in validations)
                .map(validationKey => [validationKey, validations[validationKey]]),
            ),
          ];
        })),
    };
    const v$ = useVuelidate(rules, {
      ...profileFields,
    });
    const valid = await v$.value.$validate();
    if (!valid) {
      sessionStorage.setItem('_authorizeMult', JSON.stringify(params));
      sessionStorage.setItem('_profile', JSON.stringify(profile));
      router.push('multi-user/set-profile');
    }
    return valid;
  };

  const authorizeMult = async (
    {
      keepLoggedIn,
      accountData,
      source = 'user-action',
      checkProfile = true,
    }:
    {
      keepLoggedIn: boolean;
      accountData?: AccountData;
      source?: string;
      checkProfile?: boolean;
    }
  ) => {
    if (checkProfile && Object.keys(config.profileFields).length) {
      const valid = await _checkProfile({
        keepLoggedIn,
        accountData,
        source,
      });
      if (!valid) return;
    }
    sessionStorage?.setItem('keepLoggedIn', `${keepLoggedIn}`);
    await loadUsers();
    const previousMultRoute = sessionStorage.getItem('redirect-mult') || '';
    if (/^\/or-auth/.test(previousMultRoute)) {
      sessionStorage.removeItem('redirect-mult');
      return router.push(previousMultRoute);
    }

    let accountUser, foundUserFromSSO;

    if (accountData && Object.keys(accountData).length > 0) {
      accountUser = availableUsers.value.find((user) =>
        Object.keys(accountData).some(prop => user[prop as keyof User] === accountData[prop])
      );
      foundUserFromSSO = !!accountUser;
    }

    if (availableUsers.value.length === 1) {
      accountUser = availableUsers.value[0];
    }

    const allowSelectAccount = router.currentRoute.value.query.allowSelectAccount;

    if (accountUser) {
      // log user in; get token
      try {
        await multiUserLoginUser({
          ...accountUser,
          long: keepLoggedIn,
        });
      } catch (e) {
        return router.push('/multi-user/list-users');
      }
      // accessing account
      try {
        if (isSSO.value) {
          let rootWindow;
          if (window.opener) {
            rootWindow = window.opener;
          } else if (window.self !== window.top) { // Check if current window is an iframe
            rootWindow = window.parent;
          }
          if (rootWindow) {
            return rootWindow.postMessage({
              type: 'LOGIN_SUCCESS',
              token: token(),
              accountId: accountId.value,
              source,
            }, '*');
          }
        } else {
          if (forceChangePassword.value) return router.push('/profile/security');

          const targetDestinationUrl = destinationUrl.value;
          if (targetDestinationUrl) {
            return window.location = targetDestinationUrl;
          }
          let settingRedirectUrl = await settingsService.getMergedSettings({ key: 'loginRedirectUrl' }).catch(error => {
            if (error.cause?.response?.data?.message.includes('suspended')) {
              return `${authUiUrl}/account/payments`;
            }
          });
          const defaultRedirectUrl = `${config.REPORTING_URL}/dashboard`;
          settingRedirectUrl = settingRedirectUrl ? settingRedirectUrl : defaultRedirectUrl;
          const _redirectPath = redirectPath.value ? redirectPath.value : settingRedirectUrl;
          return window.location = _redirectPath;
        }
      } catch (error) {
        _authError(error);
        throw error;
      }
    } else if (!allowSelectAccount && isSSO.value && !foundUserFromSSO) {
      const rootWindow = getRootWindow();
      if (rootWindow) {
        return rootWindow.postMessage({
          type: 'ACCOUNT_NOT_FOUND',
          availableUsers: JSON.parse(JSON.stringify(availableUsers.value)),
          username: multiUser.value?.username ?? null,
          source,
        }, '*');
      }
    }
    return router.push('/multi-user/list-users');
  };

  const recoverSession = async ({
    credentials,
    keepLoggedIn,
    verificationCode,
    userToken,
    rememberTwoFactor,
    userId,
    accountId,
    captchaToken,
    googleCode,
  }: RecoverSessionParams) => {
    try {
      let result = await authService.signIn({
        credentials,
        long: keepLoggedIn,
        verificationCode,
        userToken,
        rememberTwoFactor,
        captchaToken,
        googleCode,
      });

      if (result.tokenType === 'multi-user' && !result.twoFactorCheck) {
        result = await authService.multiUserLoginUser({
          accountId: accountId,
          id: userId,
          long: keepLoggedIn,
        });
      }

      if (result.twoFactorCheck) {
        _twoFactorCheck(true);
      } else {
        // Edgecase for single users;
        _authSuccess(result as unknown as AuthData);
      }
      return result;
    } catch (error) {
      _authError(error);
    }
  };

  const cancelTwoFactorCheck = () => {
    _twoFactorCheck(false);
  };

  const signInWithMultToken = async ({ user, keepLoggedIn }: { user: User; keepLoggedIn: boolean; }) => {
    await authService.saveMultiUser(user, keepLoggedIn);
    return authorizeMult({ keepLoggedIn });
  };

  const setDestinationUrl = (value: string | null) => {
    _setDestinationUrl(value);
  };

  const signInWithToken = async ({ token, allowGuestLogin }) => {
    return await authService.signInWithToken({
      token,
      allowGuestLogin,
    })
      .then(result => {
        // Edgecase for single users;
        _authSuccess(result as unknown as AuthData);
      })
      .catch(error => {
        console.error('ERROR', error);
        _authError(error);
      });
  };

  const loadUsers = async () => {
    const availableUsers = await authService.multiUserGetUsersList();
    _multiUserAvailableUsers(availableUsers);
  };

  const sucessAuth = (user) => {
    _authInit({
      ...user,
      authorized: true,
    });
  };

  const setRedirectUrl = (redirectPath) => {
    const currentHostname = window.location.hostname.split('.');
    const validUrl = new URL(redirectPath).hostname.split('.');
    const currentDomain = currentHostname.slice(-2).join('.');
    const validDomain = validUrl.slice(-2).join('.');

    if (currentDomain === validDomain) {
      // TODO: maybe remove method completelly.
      _setRedirectUrl(redirectPath);
    } else {
      window.location.href = '/login';
    }
  };

  const signOut = async (token?: string) => {
    await authService.signOut(token);
    authService.stopCookieMonitor();
    _signOut();
  };

  const multiUserLoginUser = async (selected: AccountsItem) => {
    _multiUserIsLogginUser(true);
    try {
      const user = await authService.multiUserLoginUser(selected);
      _authInit({
        ...user,
        authorized: true,
        initialPath: initialPath.value!,
      });
      if (user.forceChangePassword) {
        _setForceChangePassword(true);
      }
    } catch (e) {
      console.warn(e);
      throw e;
    } finally {
      _multiUserIsLogginUser(false);
    }
  };

  const validateMultiUser = async () => {
    if (multiUser.value) return;
    const _multiUser = await authService.validateMultiUserToken();
    _authSetMultiUser(_multiUser);
    if (_multiUser.forceChangePassword) {
      _setForceChangePassword(true);
    }
  };

  const toggleMultiUserMFA = () => {
    _multiUserToggleMFA();
  };

  const setSSOTwoFactor = ({ error, KEY }) => {
    _ssoNeedsTwoFactor({
      error,
      KEY,
    });
  };

  const setIsSSO = () => {
    _setIsSso();
  };

  // TODO: Remove from here, to useLoginIFrame/or w.e.
  const ssoLoginMFA = async (verificationCode: string) => {
    const user = await fetch(`${config.GLOBAL_SSO_URL}/mfa`, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        GATEWAY_URL: config.GATEWAY_URL,
        PROVIDERS_ACCOUNT_ID: config.PROVIDERS_ACCOUNT_ID,
        MFACode: verificationCode,
        KEY: sso_key.value,
      }),
    }).then(r => r.json());

    window.postMessage(user.token ? {
      type: 'SSO_SUCCESS_LOGIN',
      user,
    } : {
      type: 'SSO_TWO_FACTOR_CHECK',
      error: user.error,
      KEY: user.KEY,
    }, '*');
    if ('error' in user) {
      throw user.error;
    }
  };

  const getAccountIsPayable = async () => {
    if (config.ENABLE_PAYMENTS) {
      const paymentsStore = usePaymentsStore();
      const _isPayable = await paymentsStore.getPayableStatus().then((response) => response.billable);

      _setIsPayable(_isPayable);

      if (_isPayable) {
        paymentsStore.getCustomerAccounts();
      }
    }
  };

  const accountName = ref('');

  const getAccountName = async () => {
    accountName.value = await accountsService.getAccountData(accountId.value!)
      .then(response => response.accountName as string)
      .catch(async (error) => {
        if (error.cause?.response?.data?.message.includes('suspended') || error.message.includes('suspended')) {
          const name = (await usersService.getAccountInfo()).accountName;
          return name;
        }
        throw new Error(error);
      });
  };

  return {
    // state
    token,
    reactiveToken,
    tokenExpireTime,
    accountId,
    username,
    userId,
    error,
    authorized,
    multiUserId,
    role,
    initialPath,
    redirectPath,
    destinationUrl,
    allowGuest,
    twoFactorCheck,
    twoFactorEnabled,
    identityProvider,
    multiUserIsLoggingUser,
    multiUser,
    availableUsers,
    cookiesExpired,
    cookiesAnotherUser,
    cookiesNoRights,
    sso_needs_two_factor,
    isSSO,
    sso_key,
    permissions,
    isReadonly,
    isAdmin,
    isPayable,
    isSuspended,
    forceChangePassword,
    accountName,
    // mutations
    _authSuspended,
    _setForceChangePassword,
    // actions
    getAccountName,
    init,
    refreshSuspendedUserToken,
    signIn,
    authorizeMult,
    recoverSession,
    cancelTwoFactorCheck,
    signInWithMultToken,
    signInWithToken,
    loadUsers,
    sucessAuth,
    setRedirectUrl,
    setDestinationUrl,
    signOut,
    multiUserLoginUser,
    validateMultiUser,
    toggleMultiUserMFA,
    setSSOTwoFactor,
    setIsSSO,
    ssoLoginMFA,
    getAccountIsPayable,
    multiUserSetProfile: authService.multiUserSetProfile.bind(authService),
  };
});

export type AuthStoreType = ReturnType<typeof useAuthStore>;
